import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { ChevronDown, Search } from 'lucide-react';
import SlidingSideBar from '../../../SlidingSideBar/SlidingSideBar';
import CircularProgressBar from '../../../circular-progress';
import { fetchAuditLogs } from '../../../../helpers/auditLogs';
import { ABORT_ERROR } from '../../../../helpers/api';
import { getUserFromLocalstrorage } from '../../../../helpers/global';
import useInfiniteScroll from '../../../../hooks/useInfiniteScroll';
import ActivityGroup from './ActivityGroup';
import { collapseCompanionActivityEvents, groupActivityItems } from './activityUtils';
import './activity.scss';

const SEARCH_DEBOUNCE_MS = 300;
const EMPTY_ACTIVITY_MESSAGE = 'No activity yet for this file.';
const EMPTY_FILTERED_ACTIVITY_MESSAGE = 'No activity matches your filters.';

const ActivityList = ({ groupedItems, currentUser, loadingMore, loadMoreError, onRetryLoadMore }) => (
  <>
    {groupedItems.map(group => (
      <ActivityGroup
        key={group.label}
        label={group.label}
        items={group.items}
        currentUser={currentUser}
      />
    ))}
    {loadingMore && (
      <div className="activity-load-more" role="status" aria-busy="true" aria-label="Loading more activity">
        <span className="activity-load-more-spinner" aria-hidden="true" />
      </div>
    )}
    {!loadingMore && loadMoreError && (
      <div className="activity-load-more-error" role="alert">
        <p className="activity-load-more-error-message">{loadMoreError}</p>
        <Button variant="outline-secondary" size="sm" onClick={onRetryLoadMore}>
          Try again
        </Button>
      </div>
    )}
  </>
);

const ActivitySidebarContent = ({
  loading,
  error,
  groupedItems,
  currentUser,
  hasActiveFilters,
  loadingMore,
  loadMoreError,
  onRetryLoadMore
}) => {
  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <CircularProgressBar />
      </div>
    );
  }

  if (error) {
    return <div className="activity-error">{error}</div>;
  }

  if (!groupedItems.length) {
    if (loadingMore) {
      return (
        <div
          className="activity-load-more d-flex justify-content-center py-5"
          role="status"
          aria-busy="true"
          aria-label="Loading more activity"
        >
          <span className="activity-load-more-spinner" aria-hidden="true" />
        </div>
      );
    }

    return (
      <div className="activity-empty">
        {hasActiveFilters ? EMPTY_FILTERED_ACTIVITY_MESSAGE : EMPTY_ACTIVITY_MESSAGE}
      </div>
    );
  }

  return (
    <ActivityList
      groupedItems={groupedItems}
      currentUser={currentUser}
      loadingMore={loadingMore}
      loadMoreError={loadMoreError}
      onRetryLoadMore={onRetryLoadMore}
    />
  );
};

const ACTIVITY_TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'File', label: 'File' },
  { value: 'Lien Search', label: 'Lien Search' },
  { value: 'Estoppel', label: 'Estoppel' },
  { value: 'Survey', label: 'Survey' }
];

const ActivitySidebar = ({ visible, onClose, service }) => {
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const loadMoreAbortControllerRef = useRef(null);
  const loadMoreRequestIdRef = useRef(0);

  const currentUser = useMemo(() => getUserFromLocalstrorage(), [visible]);
  const groupedItems = useMemo(
    () => groupActivityItems(collapseCompanionActivityEvents(items)),
    [items]
  );
  const sidebarTitle = 'Activity Logs';
  const hasActiveFilters = Boolean(debouncedSearch || typeFilter);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const loadInitial = useCallback(
    async (signal, { search, type } = {}) => {
      if (!service?._id) return;

      setLoading(true);
      setError(null);
      setLoadMoreError(null);

      const { response, error: requestError } = await fetchAuditLogs(service._id, {
        signal,
        search,
        type
      });

      if (signal?.aborted) return;

      if (requestError) {
        setItems([]);
        setNextCursor(null);
        setError(requestError);
      } else {
        setItems(response?.items || []);
        setNextCursor(response?.nextCursor || null);
      }

      setLoading(false);
    },
    [service?._id]
  );

  useEffect(() => {
    if (!visible || !service?._id) return undefined;

    if (loadMoreAbortControllerRef.current) {
      loadMoreAbortControllerRef.current.abort();
    }
    loadMoreRequestIdRef.current += 1;
    setLoadingMore(false);

    const controller = new AbortController();
    setItems([]);
    setNextCursor(null);
    loadInitial(controller.signal, { search: debouncedSearch, type: typeFilter });

    return () => {
      controller.abort();
      if (loadMoreAbortControllerRef.current) {
        loadMoreAbortControllerRef.current.abort();
      }
      loadMoreRequestIdRef.current += 1;
    };
  }, [visible, service?._id, debouncedSearch, typeFilter, loadInitial]);

  useEffect(() => {
    if (visible) return;
    setSearchQuery('');
    setDebouncedSearch('');
    setTypeFilter('');
    setLoadMoreError(null);
  }, [visible]);

  const handleLoadMore = useCallback(async () => {
    if (!nextCursor || loadingMore || loading || !service?._id) return;

    if (loadMoreAbortControllerRef.current) {
      loadMoreAbortControllerRef.current.abort();
    }

    const controller = new AbortController();
    loadMoreAbortControllerRef.current = controller;
    const requestId = ++loadMoreRequestIdRef.current;

    setLoadingMore(true);
    setLoadMoreError(null);

    const { response, error: requestError } = await fetchAuditLogs(service._id, {
      cursor: nextCursor,
      search: debouncedSearch,
      type: typeFilter,
      signal: controller.signal
    });

    if (requestId !== loadMoreRequestIdRef.current) {
      return;
    }

    if (requestError === ABORT_ERROR || controller.signal.aborted) {
      setLoadingMore(false);
      return;
    }

    if (requestError) {
      setLoadMoreError(requestError);
    } else {
      setItems(previousItems => [...previousItems, ...(response?.items || [])]);
      setNextCursor(response?.nextCursor || null);
    }

    setLoadingMore(false);
  }, [nextCursor, loadingMore, loading, service?._id, debouncedSearch, typeFilter]);

  const handleLoadMoreRetry = useCallback(() => {
    setLoadMoreError(null);
    handleLoadMore();
  }, [handleLoadMore]);

  useEffect(() => {
    if (!visible || loading || loadingMore || loadMoreError || !nextCursor) return;
    if (items.length > 0 && groupedItems.length === 0) {
      handleLoadMore();
    }
  }, [
    visible,
    loading,
    loadingMore,
    loadMoreError,
    nextCursor,
    items.length,
    groupedItems.length,
    handleLoadMore
  ]);

  const { scrollContainerRef } = useInfiniteScroll({
    onLoadMore: handleLoadMore,
    hasMore: visible && Boolean(nextCursor) && !loadMoreError,
    loadingMore,
    items
  });

  return (
    <SlidingSideBar
      visible={visible}
      onClose={onClose}
      title={sidebarTitle}
      showCloseButton
      closeButtonVariant="icon"
      flush
      containerRef={scrollContainerRef}
      panelClassName="activity-sidebar-panel"
      headerClassName="activity-sidebar-header"
      width="28rem"
    >
      <div className="activity-sidebar-body">
        {!error && (
          <div className="activity-sidebar-filters">
            <div className="activity-sidebar-search">
              <Search aria-hidden="true" className="activity-sidebar-search-icon" size={16} />
              <Form.Control
                type="search"
                className="activity-sidebar-filter-input activity-sidebar-search-input"
                placeholder="Search"
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
              />
            </div>
            <div className="activity-sidebar-type-filter">
              <Form.Control
                as="select"
                className="activity-sidebar-filter-input activity-sidebar-type-filter-input"
                value={typeFilter}
                onChange={event => setTypeFilter(event.target.value)}
                aria-label="Filter by order type"
              >
                {ACTIVITY_TYPE_OPTIONS.map(option => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Control>
              <ChevronDown aria-hidden="true" className="activity-sidebar-type-filter-icon" size={14} />
            </div>
          </div>
        )}
        <ActivitySidebarContent
          loading={loading}
          error={error}
          groupedItems={groupedItems}
          currentUser={currentUser}
          hasActiveFilters={hasActiveFilters}
          loadingMore={loadingMore}
          loadMoreError={loadMoreError}
          onRetryLoadMore={handleLoadMoreRetry}
        />
      </div>
    </SlidingSideBar>
  );
};

export default ActivitySidebar;
